/** 페이지 배너 합치기 — 의존성 없는 순수 함수(tests/pageBanners.test.ts) */

export interface PageBanner {
    id: number;
    title?: string;
    imageUrl?: string | null;
    mobileImageUrl?: string | null;
    linkUrl?: string | null;
    displayOrder: number;
}

/**
 * 한 자리(상단·하단)의 배너 — 페이지 위치 배너 + "모든 페이지" 배너를 합쳐 같은 배너는 한 번만, 우선순위(displayOrder) → id 순.
 * 이미지가 없는 배너(텍스트 배너)는 목록 배너와 같이 건너뛴다.
 */
export function mergeSlot(byLocation: Record<string, PageBanner[] | undefined> | null | undefined, locations: string[]): PageBanner[] {
    if (!byLocation) return [];
    const seen = new Set<number>();
    const merged: PageBanner[] = [];
    for (const location of locations) {
        for (const banner of byLocation[location] ?? []) {
            if (!banner?.imageUrl || seen.has(banner.id)) continue;
            seen.add(banner.id);
            merged.push(banner);
        }
    }
    return merged.sort((a, b) => (a.displayOrder - b.displayOrder) || (a.id - b.id));
}

/** 홈 메인 배너 줄에 함께 넘길 위치 — 홈 상단 → 모든 페이지 상단 순(2026-10-19) */
export const HOME_MAIN_EXTRA_LOCATIONS: readonly string[] = ['HOME_TOP', 'ALL_TOP'];

/**
 * 홈 메인 배너 줄 합치기(2026-10-19) — 홈은 이미 메인 배너(HOME_MAIN) 넘김 줄이 있으니 "홈 상단"·"모든 페이지 상단" 배너를
 * 위에 따로 한 장 더 쌓지 않고 이 줄에 넣는다. 순서: HOME_MAIN(받은 순서 그대로) → HOME_TOP → ALL_TOP(각 위치 안은 우선순위 → id).
 * 같은 배너(id)는 한 번만, 이미지 없는 배너는 뺀다. 배너 id 는 그대로라 클릭·노출 기록도 원래 배너로 남는다.
 */
export function mergeHomeMain<T extends { id: number }>(
    main: readonly T[] | null | undefined,
    byLocation: Record<string, PageBanner[] | undefined> | null | undefined,
    extraLocations: readonly string[] = HOME_MAIN_EXTRA_LOCATIONS,
): (T | PageBanner)[] {
    const merged: (T | PageBanner)[] = [...(main ?? [])];
    const seen = new Set<number>(merged.map(b => b.id));
    for (const location of extraLocations) {
        for (const banner of mergeSlot(byLocation, [location])) {
            if (seen.has(banner.id)) continue;
            seen.add(banner.id);
            merged.push(banner);
        }
    }
    return merged;
}
